import {
	Paper,
	styled,
	Table,
	TableBody,
	TableContainer,
	TablePagination,
} from "@mui/material";
import { types } from "api";
import { getComparator, Order, stableSort } from "helpers/tableComparator";
import React, { useState } from "react";
import { GroupTableToolbar } from "./GroupTableToolbar";
import { StudentTableHead } from "./StudentTableHead";
import { StudentTableRow } from "./StudentTableRow";

const PREFIX = "StudentTable";

const classes = {
	root: `${PREFIX}-root`,
	paper: `${PREFIX}-paper`,
	table: `${PREFIX}-table`,
	hide: `${PREFIX}-hide`,
	button: `${PREFIX}-button`,
	errBtn: `${PREFIX}-errBtn`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		width: "100%",
	},
	[`& .${classes.paper}`]: {
		width: "100%",
		marginBottom: theme.spacing(2),
	},

	[`& .${classes.table}`]: {
		minWidth: 750,
	},

	[`& .${classes.hide}`]: {
		display: "none",
	},

	[`& .${classes.button}`]: {
		float: "left",
		margin: "10",
	},

	[`& .${classes.errBtn}`]: {
		color: theme.palette.error.main,
	},
}));

export interface StudentTableProps {
	/**
	 * Additional toolbar buttons.
	 */
	children?: JSX.Element | JSX.Element[];
	students: types.User[];
	onDownload: (ids: string[]) => void;
	title?: string;
}

export const StudentTable = ({
	children,
	students,
	onDownload,
	title = "Student List",
}: StudentTableProps): JSX.Element => {
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<keyof types.User>("firstName");
	const [selected, setSelected] = useState<string[]>([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof types.User,
	): void => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleSelectAllClick = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		if (event.target.checked) {
			const newSelecteds = students.map((n) => n.userId);
			setSelected(newSelecteds);
			// onDownload(newSelecteds);
			return;
		}
		setSelected([]);
		// onDownload([]);
	};

	const onCheckboxClick = (
		event: React.MouseEvent<unknown>,
		userid: string,
	): void => {
		const selectedIndex = selected.indexOf(userid);
		let newSelected: string[] = [];

		if (selectedIndex === -1) {
			// If the row does not been checked
			newSelected = [...selected, userid];
		} else if (selectedIndex === 0) {
			// If the row's selected record is at the begin of the list
			newSelected = [...selected.slice(1)];
		} else if (selectedIndex === selected.length - 1) {
			// If the row's selected record is at the end of the list
			newSelected = [...selected.slice(0, -1)];
		} else if (selectedIndex > 0) {
			// Other case when the row is selected
			newSelected = [
				...selected.slice(0, selectedIndex),
				...selected.slice(selectedIndex + 1),
			];
		}

		setSelected(newSelected);
		// onDownload(newSelected);
	};

	const handleDownload = (): void => {
		onDownload(selected);
		setSelected([]);
	};

	const handleChangePage = (event: unknown, newPage: number): void => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	return (
		<Root className={classes.root}>
			<Paper className={classes.paper}>
				<GroupTableToolbar
					numSelected={selected.length}
					handleDownload={handleDownload}
					title={title}
				>
					{children}
				</GroupTableToolbar>

				<TableContainer>
					<Table
						className={classes.table}
						aria-labelledby="tableTitle"
						aria-label="enhanced table"
					>
						<StudentTableHead
							numSelected={selected.length}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={students.length}
						/>
						<TableBody>
							{stableSort(students, getComparator(order, orderBy))
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((row, index) => (
									<StudentTableRow
										selected={selected}
										onCheckboxClick={onCheckboxClick}
										row={row}
										key={row.userId}
										labelId={`enhanced-table-checkbox-${index}`}
									/>
								))}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={students.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Paper>
		</Root>
	);
};
